import React, { useMemo } from "react";
import { Card, CardContent, Typography, Box, Divider } from "@mui/material";

export type MaterialTotalsBlender = {
  id: string;
  label: string;
  /**
   * Total per material label, already converted to the displayed unit.
   * A missing key means this blender does not run that material.
   */
  values: Record<string, number>;
};

type Props = {
  blenders: MaterialTotalsBlender[];
  formatValue: (v: number) => string;
  totalLabel?: string;
};

/**
 * One card per material with a per-blender breakdown and a summed total,
 * plus a final "all materials" card. With a single blender the breakdown
 * rows are dropped and each card is just the material total.
 *
 * All cards sit on a single row from `md` up, however many there are.
 */
export const MaterialTotalsCards: React.FC<Props> = ({
  blenders,
  formatValue,
  totalLabel = "Total (all materials)",
}) => {
  // Ordered union of material labels across blenders (first appearance wins).
  const materials = useMemo(() => {
    const seen: string[] = [];
    for (const b of blenders) {
      for (const m of Object.keys(b.values)) {
        if (!seen.includes(m)) seen.push(m);
      }
    }
    return seen;
  }, [blenders]);

  const showBreakdown = blenders.length > 1;

  const blenderTotals = useMemo(
    () =>
      blenders.map((b) => ({
        ...b,
        total: Object.values(b.values).reduce((s, v) => s + (v || 0), 0),
      })),
    [blenders]
  );

  const grandTotal = blenderTotals.reduce((s, b) => s + b.total, 0);

  // Material cards + the grand-total card share one row on md+.
  const columns = materials.length + 1;

  const renderRows = (rows: { key: string; label: string; value: number }[]) =>
    rows.map((r) => (
      <Box
        key={r.key}
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary" noWrap>
          {r.label}
        </Typography>
        <Typography variant="body2" noWrap>
          {formatValue(r.value)}
        </Typography>
      </Box>
    ));

  const renderCard = (
    key: string,
    caption: string,
    rows: { key: string; label: string; value: number }[],
    total: number,
    highlight = false
  ) => (
    <Card
      key={key}
      sx={{ height: "100%", ...(highlight && { bgcolor: "action.hover" }) }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 1.75 }, "&:last-child": { pb: 2 } }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", minHeight: 48, lineHeight: 1.3 }}
        >
          {caption}
        </Typography>

        {showBreakdown && (
          <>
            {renderRows(rows)}
            <Divider sx={{ my: 1 }} />
          </>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
          gap={1}
        >
          {showBreakdown && (
            <Typography variant="body2" color="text.secondary">
              Total
            </Typography>
          )}
          <Typography variant="h6" noWrap>
            {formatValue(total)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: `repeat(${columns}, minmax(0, 1fr))`,
        },
        alignItems: "stretch",
      }}
    >
      {materials.map((material) => {
        const contributors = blenderTotals.filter(
          (b) => b.values[material] != null
        );
        const total = contributors.reduce(
          (s, b) => s + (b.values[material] || 0),
          0
        );

        return renderCard(
          material,
          material,
          contributors.map((b) => ({
            key: b.id,
            label: b.label,
            value: b.values[material] || 0,
          })),
          total
        );
      })}

      {renderCard(
        "__grand_total__",
        totalLabel,
        blenderTotals.map((b) => ({
          key: b.id,
          label: b.label,
          value: b.total,
        })),
        grandTotal,
        true
      )}
    </Box>
  );
};
